import { traducirError, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, useAvisos, Ventana } from '@tallerya/ui';
import { useState } from 'react';
import { useCambiosCatalogos, type Especialidad } from './datos';

// Eliminar una especialidad que nadie tiene (#154), como `EliminarCategoria`:
// solo se ofrece cuando `isDeletable`, y si en el medio alguien la eligió en una
// ficha, el motivo se dice acá.

interface Props {
  especialidad: Especialidad | null;
  onCerrar: () => void;
}

export function EliminarEspecialidad({ especialidad, onCerrar }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosCatalogos();
  const [eliminando, setEliminando] = useState(false);
  const [problema, setProblema] = useState<string | null>(null);

  function cerrar() {
    if (eliminando) return;
    setProblema(null);
    onCerrar();
  }

  async function eliminar() {
    if (!especialidad) return;
    setEliminando(true);
    setProblema(null);
    try {
      const error = await cambios.eliminarEspecialidad(String(especialidad.id));
      if (error) {
        setProblema(error.code ? traducirError(error.code, t) : t('ui.formulario.aviso.sinRespuesta'));
        return;
      }
    } catch {
      setProblema(t('ui.formulario.aviso.sinRespuesta'));
      return;
    } finally {
      setEliminando(false);
    }
    avisar({ titulo: t('admin.catalogos.especialidades.aviso.eliminada', { nombre: especialidad.name }) });
    onCerrar();
  }

  return (
    <Ventana
      abierta={especialidad !== null}
      titulo={especialidad ? t('admin.catalogos.especialidades.eliminar.titulo', { nombre: especialidad.name }) : ''}
      descripcion={t('admin.catalogos.especialidades.eliminar.texto')}
      acciones={
        <>
          <Boton variante="texto" disabled={eliminando} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton variante="peligro" disabled={eliminando} aria-busy={eliminando} onClick={() => void eliminar()}>
            {t('admin.catalogos.eliminar')}
          </Boton>
        </>
      }
    >
      {problema && <AvisoError aviso={{ titulo: t('admin.catalogos.error.titulo'), texto: problema }} />}
    </Ventana>
  );
}
